import { CheckOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Checkbox, /* Collapse, */ Empty, Popover, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { ucfirst } from '@/utils/string';
import styles from './AttributeValuePicker.module.scss';

export interface AttributeValuePickerGroup {
  attributeId: string;
  attributeName: string;
  isColour: boolean;
  options: Array<{
    valueId: string;
    name: string;
    colorCode?: string;
  }>;
}

interface AttributeValuePickerProps {
  groups: AttributeValuePickerGroup[];
  selectedValueIds: string[];
  onChange: (valueIds: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
}

export function AttributeValuePicker({
  groups,
  selectedValueIds,
  onChange,
  loading = false,
  disabled = false,
}: AttributeValuePickerProps) {
  const [visibleGroupIds, setVisibleGroupIds] = useState<string[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);

  useEffect(() => {
    setVisibleGroupIds((current) => {
      const availableIds = groups.map((group) => group.attributeId);
      if (!current.length) return availableIds;

      const kept = current.filter((id) => availableIds.includes(id));
      const added = availableIds.filter((id) => !current.includes(id));
      return [...kept, ...added];
    });
  }, [groups]);

  const visibleGroups = useMemo(
    () => groups.filter((group) => visibleGroupIds.includes(group.attributeId)),
    [groups, visibleGroupIds],
  );

  const totalSelected = selectedValueIds.length;

  const toggleValue = (valueId: string) => {
    if (disabled) return;

    if (selectedValueIds.includes(valueId)) {
      onChange(selectedValueIds.filter((id) => id !== valueId));
      return;
    }

    onChange([...selectedValueIds, valueId]);
  };

  const toggleGroupVisibility = (attributeId: string, checked: boolean) => {
    const group = groups.find((item) => item.attributeId === attributeId);
    if (!group) return;

    if (checked) {
      setVisibleGroupIds((current) =>
        current.includes(attributeId) ? current : [...current, attributeId],
      );
      return;
    }

    setVisibleGroupIds((current) => current.filter((id) => id !== attributeId));

    const groupValueIds = new Set(group.options.map((option) => option.valueId));
    const nextSelected = selectedValueIds.filter((id) => !groupValueIds.has(id));
    if (nextSelected.length !== selectedValueIds.length) {
      onChange(nextSelected);
    }
  };

  const resetVisibleGroups = () => {
    setVisibleGroupIds(groups.map((group) => group.attributeId));
  };

  if (loading && !groups.length) {
    return (
      <div className={styles.loadingWrap}>
        <Spin size="small" />
        <span>Loading attributes…</span>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No attribute values found. Add attributes and values first."
        className={styles.empty}
      />
    );
  }

  const columnsContent = (
    <div className={styles.columnMenu}>
      <div className={styles.columnMenuHeader}>
        <span>Columns</span>
        <button type="button" className={styles.columnMenuReset} onClick={resetVisibleGroups}>
          Reset
        </button>
      </div>

      <div className={styles.columnMenuList}>
        {groups.map((group) => (
          <label key={group.attributeId} className={styles.columnMenuItem}>
            <Checkbox
              checked={visibleGroupIds.includes(group.attributeId)}
              onChange={(event) => toggleGroupVisibility(group.attributeId, event.target.checked)}
            />
            <span>{ucfirst(group.attributeName)}</span>
          </label>
        ))}
      </div>

      <p className={styles.columnMenuNote}>
        Hide attribute groups you do not need for this product.
      </p>
    </div>
  );

  // Collapse (dropdown accordion) removed — show variant options directly.
  // return (
  //   <Collapse
  //     bordered={false}
  //     className={styles.collapse}
  //     defaultActiveKey={[]}
  //     expandIconPosition="start"
  //     items={[
  //       {
  //         key: 'variant-options',
  //         label: (...),
  //         extra: (...),
  //         children: (...),
  //       },
  //     ]}
  //   />
  // );

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.collapseLabel}>
          <span className={styles.panelTitle}>Variant options</span>
          <span className={styles.panelHint}>
            Select values for each attribute group
          </span>
        </div>

        <div className={styles.panelActions}>
          {totalSelected ? (
            <span className={styles.panelBadge}>{totalSelected} selected</span>
          ) : (
            <span className={styles.panelBadgeMuted}>None selected</span>
          )}

          <Popover
            trigger="click"
            placement="bottomRight"
            open={columnsOpen}
            onOpenChange={setColumnsOpen}
            arrow={false}
            content={columnsContent}
            getPopupContainer={() => document.body}
            overlayInnerStyle={{ padding: 0 }}
          >
            <Button
              type="default"
              size="small"
              icon={<SettingOutlined />}
              className={styles.columnMenuBtn}
            >
              Columns
            </Button>
          </Popover>
        </div>
      </div>

      {visibleGroups.length ? (
        <div className={styles.rows}>
          {visibleGroups.map((group, index) => {
            const selectedInGroup = group.options.filter((option) =>
              selectedValueIds.includes(option.valueId),
            ).length;

            return (
              <div
                key={group.attributeId}
                className={`${styles.row} ${index === visibleGroups.length - 1 ? styles.rowLast : ''}`}
              >
                <div className={styles.rowLabel}>
                  <span className={styles.attributeName}>{ucfirst(group.attributeName)}</span>
                  {selectedInGroup ? (
                    <span className={styles.rowCount}>{selectedInGroup}</span>
                  ) : null}
                </div>

                <div
                  className={`${styles.rowOptions} ${group.isColour ? styles.rowOptionsColour : ''}`}
                >
                  {group.options.map((option) => {
                    const isSelected = selectedValueIds.includes(option.valueId);

                    if (group.isColour) {
                      return (
                        <button
                          key={option.valueId}
                          type="button"
                          className={`${styles.chip} ${styles.chipColour} ${isSelected ? styles.chipActive : ''}`}
                          onClick={() => toggleValue(option.valueId)}
                          disabled={disabled}
                          aria-pressed={isSelected}
                        >
                          <span
                            className={styles.swatch}
                            style={{ backgroundColor: option.colorCode || '#d9d9d9' }}
                          />
                          <span className={styles.chipLabel}>{option.name}</span>
                          {isSelected ? <CheckOutlined className={styles.chipCheck} /> : null}
                        </button>
                      );
                    }

                    return (
                      <button
                        key={option.valueId}
                        type="button"
                        className={`${styles.chip} ${isSelected ? styles.chipActive : ''}`}
                        onClick={() => toggleValue(option.valueId)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                      >
                        <span className={styles.chipLabel}>{option.name}</span>
                        {isSelected ? <CheckOutlined className={styles.chipCheck} /> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.hiddenAll}>
          No attribute groups visible. Open Columns to choose which attributes to show.
        </div>
      )}
    </div>
  );
}
